import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import type { AgentRow, AgentState } from '../../dashboard/types';
import { CLEAR_TEXT, KIND_STYLE, attentionText, resolveAttention } from '../../dashboard/vocabulary';
import { DashCard, Metric, StateChip, count } from './primitives';
import { useGmailStore } from '../../settings-gmail/store';

/**
 * The seven agents (spec 17 §5.5).
 *
 * **Always all seven, always in this order** — a missing row and a calm row would
 * look identical, so an agent never disappears from the table.
 *
 * **The figures are period-scoped; the attention cell is not** (P344), which is
 * why the card carries a `RIGHT NOW` chip even though most of it moves with the
 * picker.
 */
export function AgentsCard({ agents }: { agents: AgentRow[] }) {
  return (
    <DashCard
      title="Your agents"
      rightNow
      sub={
        <>
          The figures are for the period.{' '}
          <b className="font-semibold text-[var(--text-secondary)]">The last column is not</b> — it is what each agent
          needs right now, from a closed list of four kinds:{' '}
          <b className="font-semibold text-[var(--text-secondary)]">blocked</b>,{' '}
          <b className="font-semibold text-[var(--text-secondary)]">stalled</b>, handed off, clear.{' '}
          <b className="font-semibold text-[var(--text-secondary)]">
            A run of steps that stopped halfway looks exactly like one that finished
          </b>{' '}
          unless that column says so.
        </>
      }
    >
      <div>
        {agents.map((a, i) => (
          <AgentTableRow key={a.key} agent={a} last={i === agents.length - 1} />
        ))}
      </div>
    </DashCard>
  );
}

const STATE_CHIP: Record<AgentState, { label: string; tone: 'green' | 'red' | 'grey' }> = {
  working: { label: 'Working', tone: 'green' },
  blocked: { label: 'Blocked', tone: 'red' },
  off: { label: 'Off', tone: 'grey' },
  not_set_up: { label: 'Not set up', tone: 'grey' },
};

function AgentTableRow({ agent, last }: { agent: AgentRow; last: boolean }) {
  const gmail = useGmailStore().state;
  // AI Email needs the practice's Gmail; show that over its working state (spec 38 §4).
  const gmailChip: { label: string; tone: 'green' | 'red' | 'grey' } | null =
    agent.key === 'email' && gmail !== 'connected'
      ? { label: gmail === 'expired' ? 'Needs reconnecting' : 'Needs Gmail', tone: gmail === 'expired' ? 'red' : 'grey' }
      : null;
  const chip = gmailChip ?? STATE_CHIP[agent.state];
  /** A blocked or not-set-up row renders its name, icon and figures muted. */
  const dim = agent.state === 'blocked' || agent.state === 'not_set_up';

  return (
    <div
      className="flex items-center gap-4 px-6 py-[13px] hover:bg-[#FAFBFE]"
      style={last ? undefined : { borderBottom: '1px dotted #DFE3ED' }}
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F1F3F9]',
          dim && 'opacity-55',
        )}
      >
        <Icon kind={agent.icon as IconKind} size={15} stroke={1.8} className="text-[var(--text-muted)]" />
      </span>

      <span
        className={cn(
          'w-[208px] shrink-0 truncate text-[13.5px] font-semibold',
          dim ? 'text-[var(--text-muted)]' : 'text-[var(--text-primary)]',
        )}
      >
        {agent.name}
      </span>

      <span className="w-[104px] shrink-0">
        <StateChip label={chip.label} tone={chip.tone} />
      </span>

      {agent.figures.map((f, i) => (
        <span key={`${agent.key}-fig-${String(i)}`} className="w-[148px] shrink-0">
          <Metric value={f.value} size={16} className={dim ? 'opacity-70' : undefined} />
          {f.label === '' ? null : (
            <span className="block text-[11.5px] text-[var(--text-muted)]">{f.label}</span>
          )}
        </span>
      ))}
      {/* Keep the grid honest when an agent has fewer figures than another. */}
      {agent.figures.length < 2 ? <span className="w-[148px] shrink-0" /> : null}

      <span className="w-[118px] shrink-0 text-right">
        {agent.money ? (
          <>
            <Metric
              value={agent.money.value}
              format="money"
              size={15}
              tone="var(--money-green)"
              className={dim ? 'opacity-70' : undefined}
            />
            <span className="block text-[11.5px] text-[var(--text-muted)]">{agent.money.label}</span>
          </>
        ) : null}
      </span>

      {/* The attention cell. `RIGHT NOW`, never period-scoped. */}
      <span className="w-[300px] shrink-0">
        <AttentionCell agent={agent} />
      </span>

      <a
        href={agent.target}
        className="shrink-0 text-[13px] font-semibold text-[var(--brand-indigo)] hover:underline"
      >
        Open →
      </a>
    </div>
  );
}

/**
 * **An unrecognised `code` renders nothing at all** — not the raw code, not a
 * fallback sentence (§6.2). A made-up status is worse than a blank one.
 *
 * Precedence picks one entry, and **two kinds never appear in the same row**: if an
 * agent is blocked its stalls are real but irrelevant, because nothing is moving
 * anyway and listing them competes with the one thing somebody should go and fix.
 */
function AttentionCell({ agent }: { agent: AgentRow }) {
  const resolved = resolveAttention(agent.key, agent.attention);
  if (resolved === undefined) return null;

  const { chosen, entry, moreInKind } = resolved;
  const style = KIND_STYLE[chosen.kind];
  const text = chosen.kind === 'clear' ? CLEAR_TEXT : attentionText(entry, chosen.count);

  return (
    <a
      href={chosen.target}
      title={entry.targetLabel}
      className="inline-flex max-w-full items-start gap-1.5 rounded-[9px] px-2.5 py-1.5 text-[11.5px] font-semibold leading-[1.45]"
      style={{ background: style.bg, color: style.fg }}
    >
      <Icon kind={style.icon} size={11} stroke={2.4} className="mt-[3px] shrink-0" />
      <span className="min-w-0">
        {text}
        {/* A `blocked` row carries how long it has been true, because the age is
            the argument for going and fixing it. */}
        {chosen.kind === 'blocked' ? <> · {sinceLabel(chosen.since)}</> : null}
        {moreInKind > 0 ? <span className="font-normal"> +{count(moreInKind)} more</span> : null}
      </span>
    </a>
  );
}

/** `14 days`. The clock transition itself is computed server-side (§6.3) — this is
 * only how long the chosen fact has been true. */
function sinceLabel(since: string): string {
  const ms = Date.now() - Date.parse(since);
  if (!Number.isFinite(ms) || ms < 0) return '';
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `${String(days)} ${days === 1 ? 'day' : 'days'}`;
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return `${String(hours)}h`;
  return `${String(Math.max(1, Math.floor(ms / 60000)))}m`;
}
